import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '@components/dataEntry/button/Button';
import StaggerMotionWrapper from '@components/wrapper/StaggerMotionWrapper';
import GalleryItem from '@pages/Gallery/GalleryList/GalleryItem';
import GalleryDetailModal from '@pages/Gallery/Detail/GalleryDetailModal';
import { IgalleryItem } from '@typings/pages/gallery.interface';
import { PAGE_LAYOUT_TYPE } from '@typings/pages/pageLayout.interface';

const MonthSection = styled.div`
  margin-bottom: 32px;
`;

const MonthTitle = styled.h4`
  font-weight: 800;
  margin: 0 0 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  span {
    font-size: 12px;
    color: var(--subTextColor);
  }
`;

// 🔧 클릭 가능한 아이템 래퍼
const ClickableItem = styled.div`
  cursor: pointer;
`;

type Props = {
  items: IgalleryItem[];
  isEdit?: boolean;
  collectionPath: string;
  contentHeight: string;
  line: number;
  mobileLine?: number;
};

function MonthlyGalleryView({
  items,
  isEdit,
  collectionPath,
  contentHeight,
  line,
  mobileLine,
}: Props) {
  const [visibleGroups, setVisibleGroups] = useState(6);

  // 🔧 상세 모달 상태
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { year: number; month: number; list: IgalleryItem[] }
    >();
    items.forEach((item) => {
      const dateObj: Date =
        (item as any)?.date?.toDate?.() ||
        new Date(
          (item as any)?.date?._seconds
            ? (item as any).date._seconds * 1000
            : item?.date,
        );
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1;
      const key = `${year}-${month}`;
      if (!map.has(key)) map.set(key, { year, month, list: [] });
      map.get(key)!.list.push(item);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [items]);

  return (
    <>
      {grouped.slice(0, visibleGroups).map(({ year, month, list }) => (
        <MonthSection key={`${year}-${month}`}>
          <MonthTitle>
            {month.toString().padStart(2, '0')}
            <span>{year}</span>
          </MonthTitle>
          <StaggerMotionWrapper line={line || 6} mobileLine={mobileLine || 2}>
            {list.map((item) => (
              <ClickableItem
                key={item.id || item.objectID}
                onClick={() => {
                  setSelectedDocId(item.id || item.objectID || '');
                  setDetailOpen(true);
                }}
              >
                <GalleryItem
                  list={item}
                  isEdit={isEdit}
                  collectionPath={collectionPath}
                  contentHeight={contentHeight}
                  docLayout={PAGE_LAYOUT_TYPE.DEFAULT}
                />
              </ClickableItem>
            ))}
          </StaggerMotionWrapper>
        </MonthSection>
      ))}

      {grouped.length > visibleGroups && (
        <div style={{ margin: '8px 0 24px' }}>
          <Button shape="stroke" onClick={() => setVisibleGroups((v) => v + 4)}>
            더 보기 (나머지 {grouped.length - visibleGroups}개월)
          </Button>
        </div>
      )}

      {/* 🔧 상세 모달 */}
      {detailOpen && selectedDocId && (
        <GalleryDetailModal
          docId={selectedDocId}
          close={() => setDetailOpen(false)}
        />
      )}
    </>
  );
}

export default MonthlyGalleryView;
